<template>
  <LayoutAutenticado>
    <div class="settings-dashboard fade-in-up">
      
      <!-- Header -->
      <header class="settings-header">
        <h1 class="page-title">Configurações da Empresa</h1>
        <p class="page-subtitle">Gerencie as informações comerciais, acessos aos portais e todos os usuários da conta.</p>
      </header>

      <!-- Layout Wrapper (Tabs on Left, Content on Right) -->
      <div class="settings-layout">
        
        <!-- Navigation Menu -->
        <aside class="settings-nav-sidebar">
          <nav class="settings-nav">
            <button 
              v-for="(menu, index) in settingsMenu" 
              :key="menu.id" 
              class="nav-tab" 
              :class="{ active: activeTabIndex === index }"
              @click="activeTabIndex = index"
            >
              <div class="tab-icon">
                <i :class="menu.iconClass"></i>
              </div>
              <span class="tab-label">{{ menu.title }}</span>
            </button>
          </nav>
        </aside>

        <!-- Main Workspace (Dynamic Component Injection) -->
        <main class="settings-workspace">
          <div class="workspace-card">
            
            <div class="workspace-header">
               <h2>{{ currentMenu.title }}</h2>
               <p v-if="currentMenu.description" class="workspace-desc">{{ currentMenu.description }}</p>
            </div>

            <!-- Fade transition between changing tabs -->
            <div class="workspace-body">
              <Transition name="fade" mode="out-in">
                <div :key="currentMenu.id" class="embedded-component">
                  <component 
                    :is="currentMenu.component" 
                    :companyData="localData" 
                  />
                </div>
              </Transition>
            </div>
            
          </div>
        </main>
      </div>

    </div>

    <!-- Bootstrap Toast -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div id="liveToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header">
          <i class="bi bi-info-circle text-primary me-2"></i>
          <strong class="me-auto">Atualização do Sistema</strong>
          <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
        <div class="toast-body" id="toastBody">
          Aqui aparecerá a mensagem.
        </div>
      </div>
    </div>

  </LayoutAutenticado>
</template>

<script setup>
import { markRaw, ref, computed, onMounted } from 'vue';
import axios from '@/axios.js';
import { showToast } from '@/eventBus.js';

import LayoutAutenticado from '../components/layouts/LayoutAutenticado.vue';
import ComponenteDadosBasicos from '../components/ComponenteDadosBasicos.vue';
import ComponenteProprietario from '../components/ComponenteProprietario.vue';
import ComponenteAnalista from '../components/ComponenteAnalista.vue';
import ComponenteUsuario from '../components/ComponenteUsuario.vue';
import ComponentePorto from '../components/ComponentePorto.vue';

const localData = ref({});
const activeTabIndex = ref(0);

// Defining the menu items with icons and context description strings for the dynamic workspace header
const settingsMenu = ref([
  {
    id: 'collapseDadosBasicos',
    title: 'Dados Básicos',
    description: 'Edite as informações cadastrais e estruturais fundamentais do sistema.',
    iconClass: 'bi bi-building',
    component: markRaw(ComponenteDadosBasicos)
  },
  {
    id: 'collapseProprietario',
    title: 'Proprietário',
    description: 'Informações detalhadas do responsável legal ou proprietário do negócio.',
    iconClass: 'bi bi-person-badge',
    component: markRaw(ComponenteProprietario)
  },
  {
    id: 'collapseAnalista',
    title: 'Analista Porto Seguro',
    description: 'Contatos e definições relacionadas ao responsável pelas operações na rede Porto.',
    iconClass: 'bi bi-headset',
    component: markRaw(ComponenteAnalista)
  },
  {
    id: 'collapsePorto',
    title: 'Portal Porto Seguro',
    description: 'Credenciais ou chaves operacionais necessárias para integração e acesso.',
    iconClass: 'bi bi-shield-lock',
    component: markRaw(ComponentePorto)
  },
  {
    id: 'collapseUsuario',
    title: 'Gerenciar Usuários',
    description: 'Administração de contas com nível de acesso aos dados gerais da plataforma.',
    iconClass: 'bi bi-people',
    component: markRaw(ComponenteUsuario)
  }
]);

// Computed active tab for easy mapping
const currentMenu = computed(() => {
  return settingsMenu.value[activeTabIndex.value];
});

onMounted(async () => {
    const companyId = localStorage.getItem("idCompany");
    if(!companyId) return;

    axios.get(`/api/company/one/${companyId}`)
        .then(response => {
            localData.value = response.data.data || response.data;
            // Optionally remove success toasts on basic page load to improve UX verbosity.
            // showToast(response.data.data.message || "Dados carregados.");
        }).catch(error => {
            showToast(error?.response?.data?.message || "Erro ao buscar os dados do servidor.");
        });
});

</script>

<style scoped>
.settings-dashboard {
  width: 100%;
}

/* Header */
.settings-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1E293B;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #64748B;
  margin: 0;
}

/* Base Settings Structure */
.settings-layout {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

/* Left Sidebar Tab Nav */
.settings-nav-sidebar {
  width: 280px;
  min-width: 280px;
}

.settings-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: transparent;
  border: none;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.nav-tab:hover {
  background: #F8FAFC;
  color: #0F172A;
}

.tab-icon {
  font-size: 1.15rem;
  color: #94A3B8;
  transition: color 0.2s;
  width: 20px;
  display: flex;
  justify-content: center;
}

.tab-label {
  font-size: 0.95rem;
  font-weight: 600;
}

/* Active tab state */
.nav-tab.active {
  background: #FFF1F0; /* Branded Soft Red Background */
  color: #DF523A;      /* Branded High-Contrast Action Color */
}

.nav-tab.active .tab-icon {
  color: #DF523A;
}

/* Main Focus Workspace */
.settings-workspace {
  flex: 1;
}

.workspace-card {
  background: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.workspace-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #F1F5F9;
  background: #FAFAF9; /* Slight difference to anchor header */
}

.workspace-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  margin: 0 0 0.25rem 0;
}

.workspace-desc {
  font-size: 0.9rem;
  color: #64748B;
  margin: 0;
}

.workspace-body {
  padding: 2rem;
  flex: 1;
}

/* Reset / override for internal legacy components loaded horizontally */
:deep(.embedded-component .card-body) {
  padding: 0;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.embedded-component .card-title),
:deep(.embedded-component .card-text) {
  display: none; /* We moved title and text to the workspace-header */
}

:deep(.embedded-component .input-group) {
  margin-bottom: 1rem;
}

/* Vue Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fade-in-up {
  animation: fadeInUp 0.5s ease forwards;
}

/* Responsive Handling */
@media (max-width: 991.98px) {
  .settings-layout {
    flex-direction: column;
  }
  
  .settings-nav-sidebar {
    width: 100%;
  }

  /* Make navigation horizontal mapping side-scrollable */
  .settings-nav {
    flex-direction: row;
    overflow-x: auto;
    padding: 0.5rem;
    gap: 0.25rem;
    border-radius: 8px;
    -ms-overflow-style: none; /* IE/Edge */
    scrollbar-width: none; /* Firefox */
  }

  .settings-nav::-webkit-scrollbar {
    display: none; /* Safari/Chrome */
  }

  .nav-tab {
    white-space: nowrap;
    padding: 0.75rem 1rem;
    width: auto;
  }
  
  .workspace-body {
    padding: 1.25rem;
  }
}
</style>
